import { FormattedMessage, useIntl } from 'react-intl';
import { isAxiosError } from 'axios';
import { FormSubmitActions } from '../FormSubmitActions';
import { FormProvider, useWatch } from 'react-hook-form';
import { ErrorBanner } from '@/components/core/ErrorBanner';
import { WarningBanner } from '@/components/core/WarningBanner';
import { StudentInformation } from './StudentInformation';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { useForm } from 'react-hook-form';
import { toast } from '@/hooks/useToast';
import { StudentAddresses } from './StudentAddresses';
import { StudentGuardianEmails } from './StudentGuardianEmails';
import { StudentSchoolAddresses } from './StudentSchoolAddresses';
import { StudentDeliveryAddress } from './StudentDeliveryAddress';
import { StudentProductType } from './StudentProductType';
import { StudentCardSerial } from './StudentCardSerial';
import { SchemaType, useStudentFormSchema } from '@/hooks/useStudentFormSchema';
import { useClaimableCardSerial } from '@/hooks/useClaimableCardSerial';
import { getApiErrorMessage } from '@/helpers/apiError';
import { STUDENT_TYPE_VIEW } from '@/helpers/collectionTypes';
import type { StudentType } from '@/types/students';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { useTenantID } from '@/hooks/useTenantID';
import { useHalfProductAvailability } from '@/hooks/useHalfProductAvailability';
import {
  useCreateChildAndYouthCollectionMembers,
  useCreateCollectionMember,
  useCreateSchoolTransportationCollectionMembers,
  useCreateStudentTravelCollectionMembers,
  useUpdateCollectionMember,
  getGetCollectionMembersQueryKey,
  getGetTravellerByIDQueryKey,
  CollectionMemberDTO,
  TravellerDTO,
  useGetCollectionMembers,
  useUpdateTravellerByID,
  DepartmentAccessLevelDTO,
} from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import { omitEmptyOptionalFields } from '@/helpers/optionalFields';
import { trimData } from '@/helpers/trimData';
import { useAuth } from '@/stores/auth';

interface StudentProfileFormProps {
  student?: CollectionMemberDTO;
  traveller?: TravellerDTO;
  type: StudentType;
  collectionId?: string;
  departmentId?: string;
  onOpenChange?: (open: boolean) => void;
}

export const StudentProfileForm = ({
  student,
  traveller,
  type,
  collectionId,
  departmentId,
  onOpenChange,
}: StudentProfileFormProps) => {
  const isHalfProductAvailable = useHalfProductAvailability();
  const disableHalfProduct = !isHalfProductAvailable(type);
  const createMode = !student;
  const [editMode, setEditMode] = useState(createMode);
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();

  const userPermissions = useAuth((s) => s.permissions);
  const departmentAccess = useAuth((s) => s.departmentAccess);

  const canEdit = useMemo(() => {
    if (userPermissions?.includes('organization:editor')) return true;
    if (
      departmentId &&
      departmentAccess?.departments?.[departmentId]?.access_level ===
        DepartmentAccessLevelDTO.editor
    )
      return true;
    return false;
  }, [departmentId, userPermissions, departmentAccess]);

  const [ref] = useAutoAnimateConditional<HTMLFormElement>();

  const mode = createMode ? 'create' : 'update';
  const formSchema = useStudentFormSchema(mode, type);
  const customErrorMap = useCustomErrorMap;
  const createStudentTravel = useCreateStudentTravelCollectionMembers();
  const createSchoolTransportation =
    useCreateSchoolTransportationCollectionMembers();
  const createChildAndYouth = useCreateChildAndYouthCollectionMembers();
  const createCollectionMember = useCreateCollectionMember();
  const updateCollectionMember = useUpdateCollectionMember();
  const cardSerialField = useClaimableCardSerial();
  const updateTraveller = useUpdateTravellerByID();
  const createStudent = useMemo(() => {
    switch (type) {
      case 'travels':
        return createStudentTravel;
      case 'transportation':
        return createSchoolTransportation;
      case 'child-and-youth':
        return createChildAndYouth;
    }
  }, [
    type,
    createStudentTravel,
    createSchoolTransportation,
    createChildAndYouth,
  ]);

  const resolvedCollectionId = student?.collection_id ?? collectionId ?? '';

  const { data: existingMembers } = useGetCollectionMembers(
    resolvedCollectionId,
    {
      query: {
        enabled: createMode && !!resolvedCollectionId,
      },
    },
  );

  const defaultValues = useMemo(() => {
    if (student) {
      return {
        ...student,
        first_name: traveller?.first_name,
        last_name: traveller?.last_name,
        email: traveller?.email,
        national_identity_number: traveller?.national_identity_number,
        delivery_address: traveller?.delivery_address,
      };
    }

    if (type === 'child-and-youth') {
      return {
        collection_id: resolvedCollectionId,
        half_product: false,
      };
    }

    return {
      collection_id: resolvedCollectionId,
      guardian1_address: {
        street: '',
        zip_code: '',
        city: '',
      },
      school_name: '',
      school_address: {
        street: '',
        zip_code: '',
        city: '',
      },
      half_product: false,
    };
  }, [student, traveller, type, resolvedCollectionId]);

  const methods = useForm<SchemaType<typeof mode, typeof type>>({
    defaultValues,
    resolver: zodResolver(formSchema, {
      errorMap: customErrorMap(),
    }),
    mode: 'all',
  });

  const watchedEmail = useWatch({ control: methods.control, name: 'email' });
  const watchedNin = useWatch({
    control: methods.control,
    name: 'national_identity_number',
  });

  const duplicateMatch = useMemo(() => {
    if (!createMode || !existingMembers) return null;

    const emailTrimmed = (watchedEmail as string | undefined)
      ?.trim()
      .toLowerCase();
    const ninNormalized = (watchedNin as string | undefined)
      ?.trim()
      .replace(/-/g, '')
      .toLowerCase();

    if (!emailTrimmed && !ninNormalized) return null;

    for (const member of existingMembers) {
      const t = member.traveller;
      const emailMatch =
        emailTrimmed && t.email?.trim().toLowerCase() === emailTrimmed;
      const existingNin = t.national_identity_number
        ?.trim()
        .replace(/-/g, '')
        .toLowerCase();
      const ninMatch = ninNormalized && existingNin === ninNormalized;

      if (emailMatch || ninMatch) {
        return {
          name: `${t.first_name ?? ''} ${t.last_name ?? ''}`.trim(),
          travellerId: member.collection_member.traveller_id,
          emailMatch: !!emailMatch,
          ninMatch: !!ninMatch,
        };
      }
    }

    return null;
  }, [createMode, existingMembers, watchedEmail, watchedNin]);

  useEffect(() => {
    if (!createMode) methods.trigger();
  }, [createMode, methods]);

  const hasInvalidStoredDeliveryAddress = useMemo(() => {
    if (createMode) return false;
    const result = formSchema.safeParse(defaultValues);
    return (
      !result.success &&
      result.error.issues.some((issue) => issue.path[0] === 'delivery_address')
    );
  }, [createMode, formSchema, defaultValues]);
  const showDeliveryAddress =
    createMode || (editMode && hasInvalidStoredDeliveryAddress);

  const startEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const cancelEdit = useCallback(() => {
    if (createMode) {
      onOpenChange?.(false);
      return;
    }

    methods.reset();
    methods.trigger();
    setEditMode(false);
  }, [createMode, methods, onOpenChange]);

  const isLoading =
    createStudent.isPending ||
    createCollectionMember.isPending ||
    updateCollectionMember.isPending ||
    updateTraveller.isPending;

  const onSubmit = async (data: SchemaType<typeof mode, typeof type>) => {
    const payload = omitEmptyOptionalFields(trimData(data));

    if (createMode) {
      const suppliedCardSerial = cardSerialField.serial;
      if (suppliedCardSerial && !cardSerialField.ready) {
        return;
      }

      try {
        if (suppliedCardSerial) {
          await createCollectionMember.mutateAsync({
            data: {
              ...payload,
              card_serial: suppliedCardSerial,
            },
          });
        } else {
          await createStudent.mutateAsync({ data: [payload] });
        }
        toast.success(
          {
            title: formatMessage(
              {
                defaultMessage: '{name} skapades',
              },
              {
                name: `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim(),
              },
            ),
          },
          { autoClose: 3000 },
        );
        if (resolvedCollectionId) {
          await queryClient.invalidateQueries({
            queryKey: getGetCollectionMembersQueryKey(resolvedCollectionId),
          });
        }
        onOpenChange?.(false);
      } catch (error) {
        console.error('Error: ', error);
        const isAlreadyExists =
          isAxiosError(error) &&
          error.response?.data?.message === 'resource already exists';
        if (isAlreadyExists) {
          toast.error(
            {
              title: formatMessage({
                defaultMessage: 'Personnumret används redan',
              }),
              description: formatMessage({
                defaultMessage:
                  'En aktiv elev med detta personnummer finns redan i en annan organisation. Kontakta support om du behöver hjälp.',
              }),
            },
            { autoClose: 6000 },
          );
          return;
        }
        const fallback = formatMessage({
          defaultMessage: 'Ett fel inträffade',
        });
        const description = suppliedCardSerial
          ? getApiErrorMessage(error, fallback)
          : undefined;
        toast.error(
          {
            title: fallback,
            description:
              description && description !== fallback ? description : undefined,
          },
          { autoClose: 3000 },
        );
      }
      return;
    }

    if (student) {
      try {
        await Promise.all([
          updateCollectionMember.mutateAsync({
            collectionMemberID: student.id,
            data: payload,
          }),
          updateTraveller.mutateAsync({
            travellerID: student.traveller_id,
            data: payload,
          }),
        ]);

        toast.success(
          {
            title: formatMessage({
              defaultMessage: 'Ändringar sparades',
            }),
          },
          { autoClose: 3000 },
        );

        await queryClient.invalidateQueries({
          queryKey: getGetCollectionMembersQueryKey(student.collection_id),
        });
        await queryClient.invalidateQueries({
          queryKey: getGetTravellerByIDQueryKey(student.traveller_id),
        });

        methods.reset(trimData(methods.getValues()));
        setEditMode(false);
      } catch (error) {
        console.error('Error: ', error);
        toast.error(
          {
            title: formatMessage({
              defaultMessage: 'Ett fel inträffade',
            }),
          },
          { autoClose: 3000 },
        );
      }
    }
  };

  const dynamicContent = useMemo(() => {
    if (type === 'child-and-youth') {
      return (
        <>
          {showDeliveryAddress && (
            <StudentDeliveryAddress editMode={editMode} />
          )}
          <StudentGuardianEmails
            editMode={editMode}
            onClickButton={!createMode ? startEdit : undefined}
            canEdit={canEdit}
          />
        </>
      );
    }
    return (
      <>
        {showDeliveryAddress && <StudentDeliveryAddress editMode={editMode} />}
        <StudentAddresses
          editMode={editMode}
          onClickButton={!createMode ? startEdit : undefined}
          canEdit={canEdit}
        />
        <StudentSchoolAddresses editMode={editMode} />
        {!disableHalfProduct && <StudentProductType editMode={editMode} />}
      </>
    );
  }, [
    createMode,
    editMode,
    type,
    startEdit,
    canEdit,
    disableHalfProduct,
    showDeliveryAddress,
  ]);

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        className="flex w-full flex-col"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <div className="flex w-full flex-col gap-4">
          {createMode && (
            <WarningBanner announce={false}>
              <FormattedMessage
                defaultMessage={
                  'Elever som skapas manuellt kommer att skrivas över vid import om datan i importfilen inte matchar. Om en elev inte finns med i importfilen kommer eleven att inaktiveras automatiskt.'
                }
              />
            </WarningBanner>
          )}
          {duplicateMatch && (
            <DuplicateWarning
              name={duplicateMatch.name}
              travellerId={duplicateMatch.travellerId}
              emailMatch={duplicateMatch.emailMatch}
              ninMatch={duplicateMatch.ninMatch}
              studentType={type}
            />
          )}
          {createMode && (
            <StudentInformation
              editMode={editMode}
              onClickButton={startEdit}
              canEdit={canEdit}
            />
          )}
          {dynamicContent}
          {createMode && (
            <StudentCardSerial
              formattedSerial={cardSerialField.formattedSerial}
              errorMessage={cardSerialField.errorMessage}
              isChecking={cardSerialField.isChecking}
              isClaimable={cardSerialField.isClaimable}
              onChange={cardSerialField.handleChange}
            />
          )}
        </div>
        {editMode && (
          <>
            <p className="mt-6 text-text-700">
              {cardSerialField.isClaimable ? (
                <FormattedMessage defaultMessage="Kortet kopplas till eleven när du sparar. Om det finns en aktiv period beställs biljett automatiskt inom kort." />
              ) : (
                <FormattedMessage defaultMessage="Så fort du sparar och det finns en aktiv period kommer biljett (och eventuellt kort) att beställas automatiskt inom kort." />
              )}
            </p>
            <FormSubmitActions
              active={true}
              createMode={createMode}
              onCancel={cancelEdit}
              isLoading={isLoading}
              disabled={!!duplicateMatch || !cardSerialField.ready}
            />
          </>
        )}
      </form>
    </FormProvider>
  );
};

interface DuplicateWarningProps {
  name: string;
  travellerId: string;
  emailMatch: boolean;
  ninMatch: boolean;
  studentType: StudentType;
}

const DuplicateWarning = ({
  name,
  travellerId,
  emailMatch,
  ninMatch,
  studentType,
}: DuplicateWarningProps) => {
  const tenantId = useTenantID();

  const nameLink = (
    <Link
      to="/$tenantId/travellers/$travellerId/$view"
      params={{
        tenantId,
        travellerId,
        view: STUDENT_TYPE_VIEW[studentType],
      }}
      className="font-semibold underline"
    >
      {name}
    </Link>
  );

  return (
    <ErrorBanner>
      {emailMatch && ninMatch ? (
        <FormattedMessage
          defaultMessage="En elev med samma e-post och personnummer finns redan: {name}"
          values={{ name: nameLink }}
        />
      ) : emailMatch ? (
        <FormattedMessage
          defaultMessage="En elev med samma e-post finns redan: {name}"
          values={{ name: nameLink }}
        />
      ) : (
        <FormattedMessage
          defaultMessage="En elev med samma personnummer finns redan: {name}"
          values={{ name: nameLink }}
        />
      )}
    </ErrorBanner>
  );
};
